import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

function useCanvas() {
  const ref = useRef(null)

  if (ref.current) return ref.current

  const canvas = document.createElement('canvas')
  ref.current = canvas.getContext('2d')
  return ref.current
}

export const TruncateMiddleText = ({
  text,
  width = 0,
  offset = 8,
  widthOffSet = 0,
  ellipsis = '...'
}) => {
  const [targetWidth, setTargetWidth] = useState(0)
  const [shouldTruncate, setShouldTruncate] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const containerRef = useRef(null)
  const canvas = useCanvas()

  const setupCanvas = useCallback(() => {
    if (!containerRef.current) return

    const style = window.getComputedStyle(containerRef.current)

    canvas.font = [
      style['font-weight'],
      style['font-style'],
      style['font-size'],
      style['font-family']
    ].join(' ')
  }, [canvas])

  const calcTargetWidth = useCallback(() => {
    let targetW

    if (width) {
      targetW = width - widthOffSet
    } else {
      targetW =
        containerRef.current?.getBoundingClientRect().width - widthOffSet
    }

    setTargetWidth(targetW)
    const measureWidth = canvas.measureText(text).width
    setShouldTruncate(targetW < measureWidth)
    setTruncated(true)
  }, [canvas, text, width, widthOffSet])

  useEffect(() => {
    setupCanvas()
    calcTargetWidth()
  }, [calcTargetWidth, setupCanvas])

  useEffect(() => {
    const oB = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const containerWidth = entries[0].contentRect.width

        setTargetWidth(containerWidth - widthOffSet)

        // Recalculate truncation when width changes
        const measureWidth = canvas.measureText(text).width
        setShouldTruncate(containerWidth - widthOffSet < measureWidth)
        setTruncated(true)
      }
    })

    if (containerRef.current) {
      oB.observe(containerRef.current)
    }

    return () => {
      oB.disconnect()
    }
  }, [canvas, text, widthOffSet])
  const calculatedText = useMemo(() => {
    if (!shouldTruncate) return text

    const len = text.length
    const tail = text.slice(len - offset, len)
    let head

    let end = len - offset
    let start = 0

    while (start < end - 1) {
      const curr = Math.floor((end - start) / 2 + start)
      head = text.slice(0, curr)
      if (canvas.measureText(head + ellipsis + tail).width < targetWidth) {
        start = curr
      } else {
        end = curr
      }
    }
    head = text.slice(0, start || 1)
    return head + ellipsis + tail
  }, [canvas, ellipsis, offset, shouldTruncate, targetWidth, text])

  return (
    <div
      ref={containerRef}
      style={{
        width: width || '100%',
        whiteSpace: 'nowrap'
      }}
    >
      {truncated && calculatedText}
    </div>
  )
}

export default TruncateMiddleText
